import { ValueFormatterParams } from "ag-grid-community";
import { CSSProperties, useLayoutEffect, useState } from "react";
import Checkbox from "../checkbox/checkbox";
import { CurrencyEnum } from "../common-types/common-types";
import Tooltip from "../tooltip/tooltip";
import { useTranslation } from "react-i18next";
import { AvatarLetter } from "../avatar/avatar";
import { ColumnConfig, TableConfig } from "../simple-table/simple-table";
import { calculatePriceToRenewal, calculateRecurringPrice } from "../helpers/helpers";

export const GRID_BREAK_POINT = 1200;
export const MOBILE_BREAK_POINT = 767;

export enum FilterTypeEnum {
  STRING = "agTextColumnFilter",
  NUMBER = "agNumberColumnFilter",
  DATE = "agDateColumnFilter",
  //BOOLEAN = "booleanFilter",
  CHOICE = "agSetColumnFilter"
}

export interface HeaderConfig {
  renderer?: string | ((params: ValueFormatterParams) => void),
  headerRenderer?: any,
  rendererParams?: any,
  width?: number,
  minWidth?: number,
  flex?: number,
  valueFormatter?: any,
  suppressSizeToFit?: boolean,
  cellStyle?: any,
  cellClass?: string,
  headerClass?: string,
  hide?: boolean,
  sort?: string,
  getQuickFilterText?: (params: ValueFormatterParams) => string,
  comparator?: (valueA: any, valueB: any, nodeA: any, nodeB: any, isInverted: boolean) => number,
  summary?: (header: any, data: any) => void
}

export enum HeaderSizeEnum {
  EXTRA_SMALL,
  SMALL,
  MEDIUM,
  LARGE,
  EXTRA_LARGE
}

enum HeaderTypeEnum {
  TEXT,
  BOOLEAN,
  DATE_AND_TIME,
  CURRENCY,
  ICON,
  AVATAR
}

export interface TableHeader {
  field: string,
  headerName: string,
  type: HeaderTypeEnum,
  size?: HeaderSizeEnum,
  config?: HeaderConfig
}
export function useWindowSizeHook() {
  const [size, setSize] = useState([0, 0]);
  useLayoutEffect(() => {
    function updateSize() {
      setSize([window.innerWidth, window.innerHeight]);
    }
    window.addEventListener('resize', updateSize);
    updateSize();
    return () => window.removeEventListener('resize', updateSize);
  }, []);
  return size;
}

// export function CreateHeadersCentered(headers: Array<TableHeader>, viewWidth?: number, definedWidth?: boolean, commonCellStyle?: CSSProperties) {
//   const cellStyleCentered: CSSProperties = {
//     display: 'flex',
//     alignItems: 'center',
//     overflow: 'hidden'
//   };
//   const mergedCellStyle = { ...cellStyleCentered, ...commonCellStyle }
//   return CreateHeaders(headers, viewWidth, definedWidth, mergedCellStyle)
// }

export function CreateHeaders(headers: Array<TableHeader>, viewWidth?: number, definedWidth?: boolean, commonCellStyle?: CSSProperties) {
  const cellStyleCentered: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    overflow: 'hidden'
  };
  const mergedCellStyle = { ...cellStyleCentered, ...commonCellStyle }
  return headers.map(h => {
    if (h.type === HeaderTypeEnum.BOOLEAN) return CreateBooleanHeader(h.field, h.headerName, h.config, mergedCellStyle);
    else if (h.type === HeaderTypeEnum.DATE_AND_TIME) return CreateTextHeader(h.field, h.headerName, h.size, h.config, !definedWidth, mergedCellStyle);
    else if (h.type === HeaderTypeEnum.ICON) return CreateIconHeader(h.field, h.headerName);
    else if (h.type === HeaderTypeEnum.AVATAR) return CreateAvatarHeader(h.field, h.headerName);
    // else if (h.type === HeaderTypeEnum.CURRENCY) return CreateCurrencyHeader(h.field, h.headerName, h.size, h.config, !definedWidth)
    else return CreateTextHeader(h.field, h.headerName, h.size, h.config, !definedWidth, mergedCellStyle);
  });
}
export function CreateSummaryRow(headers: Array<TableHeader>) {
  const row = headers.map(h => {
    return {
      [h.field]: 1,
    }
  });
  return [row];
}

export function textHeader(field: string, headerName: string, size?: HeaderSizeEnum, config?: HeaderConfig): TableHeader {
  return {
    field: field,
    headerName: headerName,
    size: size,
    config: config,
    type: HeaderTypeEnum.TEXT
  }
}

export function booleanHeader(field: string, headerName: string, config?: HeaderConfig): TableHeader {
  return {
    field: field,
    headerName: headerName,
    config: config,
    type: HeaderTypeEnum.BOOLEAN
  }
}

const iconUrlCellRenderer = (props: any) => (
  <img src={props.data.iconUrl} alt="" width="40" height="40" />
);
export function iconHeader(field: string, headerName: string, config?: HeaderConfig): TableHeader {
  return {
    field: field,
    headerName: headerName,
    config: config || {
      renderer: iconUrlCellRenderer
    },
    type: HeaderTypeEnum.ICON
  }
}

export function avatarHeader(field: string, headerName: string, config?: HeaderConfig): TableHeader {
  return {
    field: field,
    headerName: headerName,
    config: config,
    type: HeaderTypeEnum.AVATAR
  }
}
export const dateFormatter = (date: string | null | undefined | Date | number, onlyDate?: boolean) => {
  if (!date) return "Ingen dato";
  const dateIsUnix = date.toString().length === 10;

  let dateObject = new Date(date);
  if (typeof date === 'number' && dateIsUnix) dateObject = new Date(date * 1000);

  const hours = ("0" + dateObject.getHours()).slice(-2);
  const minutes = ("0" + dateObject.getMinutes()).slice(-2);
  const seconds = ("0" + dateObject.getSeconds()).slice(-2);
  const formattedTime = hours + ':' + minutes + ':' + seconds;
  const options: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  };
  if (onlyDate) return dateObject.toLocaleDateString('nb-NO', options);
  return dateObject.toLocaleDateString('nb-NO', options) + " " + formattedTime;
}

export function minuteToHourConverter(timeInMinutes: number, defaultMessage: string) {
  if (!timeInMinutes) return defaultMessage;
  const hours = timeInMinutes >= 0 ? Math.floor(timeInMinutes / 60) : Math.ceil(timeInMinutes / 60);
  const minutes = (timeInMinutes % 60);

  const padding = (Math.abs(minutes) < 10) && (hours !== 0) ? '0' : '';
  const prefix = minutes < 0 && hours === 0 ? '-' : '';

  const hoursText = hours === 0 ? '' : `${hours}t`;
  const minutesText = prefix + `${padding + Math.abs(minutes).toFixed(0)}m`;

  return hoursText + ' ' + minutesText;
}

export const currencyFormatter = (params: any) => {
  const formatter = new Intl.NumberFormat('nb-NO', {
    style: 'currency',
    currencyDisplay: "code",
    currency: params.currency || CurrencyEnum.NOK,
  });
  return formatter.format(params.value);
};

export const currencyMultiplierFormatter = (params: any) => {
  const formatter = new Intl.NumberFormat('nb-NO', {
    style: 'currency',
    currencyDisplay: "code",
    currency: params.currency || CurrencyEnum.NOK, minimumFractionDigits: 2
  });
  return formatter.format(params.value * parseInt(params.data.newSeats));
};

export const toCurrency = (number: number, currency?: CurrencyEnum) => {
  const formatter = new Intl.NumberFormat('nb-NO', {
    style: 'currency',
    currencyDisplay: "code",
    currency: currency || CurrencyEnum.NOK,
    minimumFractionDigits: 2
  });
  return formatter.format(number);
}

export const recurringPriceFormatter = (params: any) => {
  const total = calculateRecurringPrice(
    params.data.recurringDiscountPercent,
    params.value,
    params.data.newSeats,
  );
  return total > 0 ? toCurrency(total) : 0;
}

export const priceToRenewalFormatter = (params: any) => {
  const total = calculatePriceToRenewal(
    params.data.oneTimeDiscountAmount,
    params.value,
    params.data.newSeats,
  );
  return total > 0 ? toCurrency(total) : 0;
}

export const decimalFormatter = (params: ValueFormatterParams): string => {
  if (params.value === 0) return '0';
  return params.value.toFixed(2);
};

export const getLicenseTypeIntToString = (params: ValueFormatterParams) => {
  switch (params.value) {
    case 0:
      return "licenseType.slm";
    case 1:
      return "licenseType.nlm";
    case 2:
      return "licenseType.namedUser";
    case 3:
      return "licenseType.oneTime";
    default:
      return "licenseType.unknown";
  }
};

export const getLicenseTypeStringToString = (params: ValueFormatterParams) => {
  switch (params.value) {
    case "SLM":
      return "licenseType.slm";
    case "NLM":
      return "licenseType.nlm";
    case "NAMED_USER":
      return "licenseType.namedUser";
    case "ONE_TIME":
      return "licenseType.oneTime";
    default:
      return "licenseType.unknown";
  }
};

const dateAndTimeRenderer = (params: ValueFormatterParams) => {
  if (params.data.isSummary) return null;
  return dateFormatter(params.value);
}
export function dateAndTimeHeader(field: string, headerName: string, config?: HeaderConfig): TableHeader {
  if (config && !config.renderer) config.renderer = dateAndTimeRenderer;
  return {
    field: field,
    headerName: headerName,
    size: HeaderSizeEnum.MEDIUM,
    type: HeaderTypeEnum.DATE_AND_TIME,
    config: config || {
      renderer: dateAndTimeRenderer
    }
  }
}

const dateRenderer = (params: ValueFormatterParams) => {
  if (params.data.isSummary) return null;
  return dateFormatter(params.value, true);
}
export function dateHeader(field: string, headerName: string, config?: HeaderConfig): TableHeader {
  if (config && !config.renderer) config.renderer = dateRenderer;
  return {
    field: field,
    headerName: headerName,
    size: HeaderSizeEnum.MEDIUM,
    type: HeaderTypeEnum.DATE_AND_TIME,
    config: config || {
      renderer: dateRenderer
    }
  }
}

const currencyRenderer = (params: ValueFormatterParams) => {
  return <div>{toCurrency(params.value, params.data.currency || CurrencyEnum.NOK)}</div>;
}

export function currencyHeader(field: string, headerName: string, size: HeaderSizeEnum, config?: HeaderConfig): TableHeader {
  if (config && !config.renderer) config.renderer = currencyRenderer;
  return {
    field: field,
    headerName: headerName,
    size: size,
    type: HeaderTypeEnum.CURRENCY,
    config: config || {
      renderer: currencyRenderer
    }
  }
}

export const commentMultipleLinesRenderer = (text: string) => (<>
  <Tooltip arrow title={text || ''}>
    <div style={{
      lineHeight: 'normal',
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical',
      whiteSpace: 'normal',
      maxHeight: '50px'
    }}>
      {text}
    </div>
  </Tooltip>
</>);

function CreateTextHeader(field: string, headerName: string, size?: HeaderSizeEnum, config?: HeaderConfig, useFlex?: boolean, commonCellStyle?: CSSProperties) {
  let width;
  let minWidth;
  let flex = 0;

  if (size === HeaderSizeEnum.EXTRA_SMALL) {
    if (useFlex) flex = config?.flex || 1;
    width = 90;
    minWidth = 80;
  }
  else if (size === HeaderSizeEnum.SMALL) {
    if (useFlex) flex = config?.flex || 2;
    width = 120;
    minWidth = 100;
  }
  else if (size === HeaderSizeEnum.LARGE) {
    if (useFlex) flex = config?.flex || 4;
    width = 250;
    minWidth = 150;
  }
  else if (size === HeaderSizeEnum.EXTRA_LARGE) {
    if (useFlex) flex = config?.flex || 6;
    width = 350
    minWidth = 250;
  }
  else {
    if (useFlex) flex = config?.flex || 3;
    width = 200;
    minWidth = 120;
  }
  return {
    field: field,
    headerName: headerName,
    cellRenderer: config?.renderer,
    cellRendererParams: config?.rendererParams,
    cellStyle: { ...commonCellStyle, ...config?.cellStyle },
    cellClass: config?.cellClass,
    headerClass: config?.headerClass,
    width: config?.width || width,
    minWidth: config?.minWidth || minWidth,
    flex: flex,
    valueFormatter: config?.valueFormatter,
    suppressSizeToFit: config?.suppressSizeToFit || false,
    headerComponent: config?.headerRenderer,
    autoHeight: true,
    filter: FilterTypeEnum.CHOICE,
    hide: config?.hide,
    sort: config?.sort,
    comparator: config?.comparator,
    getQuickFilterText: config?.getQuickFilterText,
    summary: config?.summary
  };
}

export function CreateIconHeader(field: string, headerName: string) {
  const cellStyleCentered: CSSProperties = {
    display: 'flex',
    alignItems: 'center'
  };

  return {
    field: field,
    headerName: headerName,
    flex: 0,
    width: 80,
    cellStyle: { ...cellStyleCentered },
    cellRenderer: iconUrlCellRenderer
  }
}

export function CreateAvatarHeader(field: string, headerName: string) {
  const cellStyleCentered: CSSProperties = {
    display: 'flex',
    alignItems: 'center'
  };

  return {
    field: field,
    headerName: headerName,
    flex: 0,
    width: 80,
    cellStyle: { ...cellStyleCentered },
    cellRenderer: (props: any) => {
      const { t } = useTranslation();
      return <AvatarLetter>{props.data.name || t('renderers.unknownName')}</AvatarLetter>
    }
  }
}

export function CreateBooleanHeader(field: string, headerName: string, config?: HeaderConfig, commonCellStyle?: CSSProperties) {
  return {
    field: field,
    headerName: headerName,
    cellRenderer: config?.renderer || ((params: ValueFormatterParams) => {
      if (params.data.isSummary) return null;
      return <Checkbox
        checked={params.value}
        disabled={true}
      />
    }),
    width: 100,
    minWidth: 80,
    flex: 0,
    suppressSizeToFit: false,
    hide: config?.hide,
    filter: FilterTypeEnum.CHOICE
  }
}

export function CreateCurrencyHeader(field: string, headerName: string) {
  return {
    field: field,
    headerName: headerName,
    cellRenderer: currencyRenderer,
    width: 200,
    minWidth: 200,
    maxWidth: 250,
    flex: 2,
    suppressSizeToFit: false,
    filter: FilterTypeEnum.CHOICE
  }
}
export function SumRows(header: any, data: any[]) {
  const field = header.field;
  const sum: number = data.reduce((startValue, d) => startValue + d[field], 0);
  return (sum % 1 === 0) ? sum.toFixed(0) : sum.toFixed(2);
}
export const handleSaveTable = (params: any, tableConfig: TableConfig) => {
  if (!tableConfig) return;
  const api = params.columnApi;
  const gridApi = params.api;
  const filterModel = gridApi.getFilterModel();
  const columnState = api.getColumnState();
  const values: ColumnConfig[] = columnState.map((column: any) => {
    const colConfig: ColumnConfig = {
      columnName: column.colId,
      isHidden: column.hide,
      sortDirection: column.sort
    }
    return colConfig
  });
  tableConfig.onSaveTableConfig(values, filterModel);
};